import { useQuery } from "@tanstack/react-query";
import { useAuth } from "../helper/useAuth";
import { httpService } from "../../services/httpService";
import { API_CONFIG } from "../../shared/constants";
import { AdmobApp, App } from "../../features/AppCountryReports/interface";
import {
  getCachedReport,
  setCachedReport,
  evictStaleReports,
} from "../../services/reportCacheService";
import dayjs from "dayjs";

interface CountryReportsResponse {
  current_page: number;
  from: number;
  last_page: number;
  per_page: number;
  to: number;
  total: number;
  results: {
    admob_apps: AdmobApp[];
    apps: App[];
    average_actual_usd: string;
  };
}

interface CountryReportsParams {
  startDate: Date;
  endDate: Date;
  delay?: number;
}

interface CountryReportsResult {
  admob_apps: AdmobApp[];
  apps: App[];
  average_actual_usd: string;
}

export const useCountryReports = (params: CountryReportsParams) => {
  const { selectedIntegration } = useAuth();
  const { startDate, endDate } = params;

  const today = dayjs().startOf("day").format("YYYY-MM-DD");
  const from = dayjs(startDate).format("YYYY-MM-DD");
  const to = dayjs(endDate).format("YYYY-MM-DD");

  const sleep = (ms: number) =>
    new Promise((resolve) => setTimeout(resolve, ms));

  return useQuery({
    queryKey: ["countryReports", selectedIntegration?.value, from, to, today],
    queryFn: async () => {
      const cacheKey = `countryReports:${selectedIntegration!.value}:${from}:${to}:${today}`;

      // Remove any entries from previous days (best-effort, non-blocking for UX)
      evictStaleReports(cacheKey);

      // Serve from IndexedDB if already fetched today — survives page reloads
      const cached = await getCachedReport<CountryReportsResult>(cacheKey);
      if (cached) return cached;

      // --- Fresh fetch: paginate through all pages ---
      const perPage = 2000;
      let page = 1;
      let allAdmobApps: AdmobApp[] = [];
      let allApps: App[] = [];
      let average_actual_usd = "";
      let lastPage = 1;
      let isFirst = true;

      do {
        const response = await httpService.get<CountryReportsResponse>(
          API_CONFIG.path.countryReports,
          {
            page,
            per_page: perPage,
            from,
            to,
            tracking_id: selectedIntegration?.value as string,
          }
        );

        if (isFirst) {
          average_actual_usd = response.results.average_actual_usd;
          lastPage = response.last_page;
          isFirst = false;
        }

        allAdmobApps = allAdmobApps.concat(response.results.admob_apps);
        allApps = allApps.concat(response.results.apps);
        page++;

        if (page <= lastPage) {
          await sleep(params.delay ?? 100);
        }
      } while (page <= lastPage);

      const uniqueApps = Array.from(
        new Map(allApps.map((app) => [app.app_slug, app])).values()
      );

      const result: CountryReportsResult = {
        admob_apps: allAdmobApps,
        apps: uniqueApps,
        average_actual_usd,
      };

      // Persist to IndexedDB — handles multi-MB payloads without quota issues
      await setCachedReport(cacheKey, result);

      return result;
    },
    enabled: !!selectedIntegration?.value,
    // React Query in-memory cache also lives until end of day
    staleTime: dayjs().endOf("day").diff(dayjs()),
    gcTime: dayjs().endOf("day").diff(dayjs()),
  });
};
